import React from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { hideModal } from "../../../actions/actionCreators";
import MaterialModal from "../MaterialModal";
import withModal from "../withModal";

const fileBigSizeUploadWarningModalProps = {
    // Injected from Redux
    modalProps: PropTypes.shape({
        continue: PropTypes.func.isRequired,
        type: PropTypes.string.isRequired,
        columnName: PropTypes.string,
        replaceUpload: PropTypes.bool,
    }).isRequired,
    isOpen: PropTypes.bool.isRequired,
};

type FileBigSizeUploadWarningModalProps = PropTypes.InferProps<typeof fileBigSizeUploadWarningModalProps>;

const FileBigSizeUploadWarningModal: React.FC<FileBigSizeUploadWarningModalProps> = ({
    modalProps,
    isOpen,
}: FileBigSizeUploadWarningModalProps) => {
    const dispatch = useDispatch();

    const hide = () => dispatch(hideModal("FILE_BIG_SIZE_UPLOAD_WARNING"));

    const continueRequest = () => {
        modalProps.continue();
        hide();
    };

    const warningMessage = () => {
        let message = <div></div>;
        switch (modalProps.type) {
            case "Submit File":
                message = (
                    <div>
                        Uploading a file larger than 1.5GB could cause performance issues while processing your file.
                    </div>
                );
                break;
            case "Change Company":
                message = (
                    <div>
                        You have a file uploading in the background. Changing companies may lead to performance issues.
                    </div>
                );
                break;
            case "Max length exceeded":
                message = (
                    <div>Max length exceeded for ${modalProps.columnName}. Any alteration will truncate the data.</div>
                );
                break;
            case "Third Party Validation":
                message = (
                    <div>
                        Your company does not support the ability to activate an audience from two or more Data
                        Providers (excluding Experian Data Assets). To continue, please update your selections.
                    </div>
                );
                break;
            case "Validation Indirect Destination":
                message = (
                    <div>
                        We cannot send any Partner Audience data to the selected destination.
                        <br />
                        <br /> Please remove the Partner Audience data or select another destination to activate your audience.
                        <br />
                        <br />
                        Please reach out to <a href="mailto:audiences@experian.com">audiences@experian.com</a> if you
                        have any questions.
                    </div>
                );
                break;
            case "Inactive Marketplace Data":
                message = (
                    <div>
                        Audience Engine’s marketplace data is not available at this time. To continue, please update
                        your selections.
                    </div>
                );
                break;
            default:
                break;
        }

        if (
            !modalProps.replaceUpload &&
            modalProps.type != "Third Party Validation" &&
            modalProps.type != "Inactive Marketplace Data" &&
            modalProps.type != "Validation Indirect Destination"
        ) {
            message = (
                <div>
                    {message}
                    <br />
                    <br />
                    Refreshing pages before the upload completes may also stop your upload. Are you sure you want to
                    continue?
                </div>
            );
        }

        return message;
    };

    const warningTitle = () => {
        let message = "WARNING: ";
        switch (modalProps.type) {
            case "Submit File":
                message += "Large File Submission";
                break;
            case "Change Company":
                message += "Large File Uploading";
                break;
            case "Max length exceeded":
                message += "Max length exceeded";
                break;
            case "Third Party Validation":
                message = "Third Party Validation";
                break;
            case "Inactive Marketplace Data":
                message = "Inactive Marketplace Data";
                break;
            case "First Party Data to Indirect Destination":
                message = "First Party Data to Indirect Destination";
                break;
            default:
                message += "";
                break;
        }

        message += "!";
        return message;
    };

    const primaryButtonText = () => {
        let message = "";
        switch (modalProps.type) {
            case "Submit File":
                message += "Continue";
                break;
            case "Change Company":
                message += "Continue";
                break;
            case "Max length exceeded":
                message += "Continue";
                break;
            case "Third Party Validation":
                message += "Ok";
                break;
            case "Inactive Marketplace Data":
                message += "Ok";
                break;
            case "First Party Data to Indirect Destination":
                message += "Ok";
                break;
            default:
                message += "Continue";
                break;
        }
        return message;
    };

    return (
        <MaterialModal
            title={warningTitle()}
            isOpen={isOpen}
            onClose={hide}
            id="file-big-size-upload-warning"
            maxWidth="sm"
            primaryButtonText={primaryButtonText()}
            onPrimaryButtonRequest={() => continueRequest()}
        >
            <span>{warningMessage()}</span>
        </MaterialModal>
    );
};

export default withModal(FileBigSizeUploadWarningModal, "FILE_BIG_SIZE_UPLOAD_WARNING");
